import React, { useRef, useEffect } from "react";

/**
 * Canvas-based medical/pharma animation overlay for the Hero section.
 * Renders DNA strands, floating molecules, hexagons, and medical crosses
 * on a transparent canvas that sits on top of the gradient background.
 */
const HeroBioCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;

    const resize = () => {
      canvas.width = canvas.parentElement.offsetWidth;
      canvas.height = canvas.parentElement.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const w = () => canvas.width;
    const h = () => canvas.height;

    // Molecules — MORE and MORE VISIBLE
    const molecules = Array.from({ length: 30 }, () => ({
      x: Math.random() * 1920,
      y: Math.random() * 1080,
      r: 3 + Math.random() * 5,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.4,
      opacity: 0.12 + Math.random() * 0.18,
      sats: Math.floor(1 + Math.random() * 3),
      satDist: 10 + Math.random() * 12,
      satAngle: Math.random() * Math.PI * 2,
      satSpeed: (0.004 + Math.random() * 0.008) * (Math.random() > 0.5 ? 1 : -1),
    }));

    // DNA helixes — MORE
    const helixes = Array.from({ length: 4 }, () => ({
      x: Math.random() * 1920,
      yStart: Math.random() * 400,
      length: 300 + Math.random() * 300,
      amplitude: 18 + Math.random() * 20,
      frequency: 0.025 + Math.random() * 0.015,
      phase: Math.random() * Math.PI * 2,
      speed: 0.006 + Math.random() * 0.004,
      opacity: 0.1 + Math.random() * 0.08,
      drift: (Math.random() - 0.5) * 0.15,
    }));

    // Hexagons — MORE VISIBLE
    const hexagons = Array.from({ length: 10 }, () => ({
      x: Math.random() * 1920,
      y: Math.random() * 1080,
      size: 10 + Math.random() * 22,
      rotation: Math.random() * Math.PI,
      rotSpeed: (Math.random() - 0.5) * 0.004,
      vy: -0.1 - Math.random() * 0.2,
      vx: (Math.random() - 0.5) * 0.12,
      opacity: 0.08 + Math.random() * 0.1,
    }));

    // Medical crosses — MORE VISIBLE
    const crosses = Array.from({ length: 8 }, () => ({
      x: Math.random() * 1920,
      y: Math.random() * 1080,
      size: 6 + Math.random() * 10,
      opacity: 0.08 + Math.random() * 0.08,
      vy: -0.04 - Math.random() * 0.08,
      vx: (Math.random() - 0.5) * 0.06,
      rotation: Math.random() * 0.5,
      rotSpeed: (Math.random() - 0.5) * 0.002,
    }));

    const CONNECTION_DIST = 250;

    const draw = () => {
      const W = w();
      const H = h();
      ctx.clearRect(0, 0, W, H);

      // DNA
      helixes.forEach((helix) => {
        helix.phase += helix.speed;
        helix.x += helix.drift;
        if (helix.x < -60) helix.x = W + 60;
        if (helix.x > W + 60) helix.x = -60;

        const steps = Math.floor(helix.length / 4);
        for (let s = 0; s < steps; s++) {
          const t = s / steps;
          const y = helix.yStart + t * helix.length;
          const x1 = helix.x + Math.sin(t * helix.length * helix.frequency + helix.phase) * helix.amplitude;
          const x2 = helix.x + Math.sin(t * helix.length * helix.frequency + helix.phase + Math.PI) * helix.amplitude;

          ctx.beginPath();
          ctx.arc(x1, y, 1.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${helix.opacity})`;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(x2, y, 1.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${helix.opacity * 0.7})`;
          ctx.fill();

          if (s % 5 === 0) {
            ctx.beginPath();
            ctx.moveTo(x1, y);
            ctx.lineTo(x2, y);
            ctx.strokeStyle = `rgba(255,255,255,${helix.opacity * 0.4})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      });

      // Hexagons
      hexagons.forEach((hex) => {
        hex.y += hex.vy;
        hex.x += hex.vx;
        hex.rotation += hex.rotSpeed;
        if (hex.y < -30) { hex.y = H + 30; hex.x = Math.random() * W; }

        ctx.save();
        ctx.translate(hex.x, hex.y);
        ctx.rotate(hex.rotation);
        ctx.beginPath();
        for (let v = 0; v < 6; v++) {
          const angle = (Math.PI / 3) * v - Math.PI / 6;
          const px = Math.cos(angle) * hex.size;
          const py = Math.sin(angle) * hex.size;
          v === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.strokeStyle = `rgba(255,255,255,${hex.opacity})`;
        ctx.lineWidth = 0.8;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(0, 0, hex.size * 0.5, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,255,255,${hex.opacity * 0.4})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
        ctx.restore();
      });

      // Molecules
      molecules.forEach((mol) => {
        mol.x += mol.vx;
        mol.y += mol.vy;
        mol.satAngle += mol.satSpeed;
        if (mol.x < -20) mol.x = W + 20;
        if (mol.x > W + 20) mol.x = -20;
        if (mol.y < -20) mol.y = H + 20;
        if (mol.y > H + 20) mol.y = -20;

        ctx.beginPath();
        ctx.arc(mol.x, mol.y, mol.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${mol.opacity})`;
        ctx.fill();

        // Glow
        const grd = ctx.createRadialGradient(mol.x, mol.y, 0, mol.x, mol.y, mol.r * 3);
        grd.addColorStop(0, `rgba(255,255,255,${mol.opacity * 0.3})`);
        grd.addColorStop(1, `rgba(255,255,255,0)`);
        ctx.beginPath();
        ctx.arc(mol.x, mol.y, mol.r * 3, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();

        for (let s = 0; s < mol.sats; s++) {
          const angle = mol.satAngle + (Math.PI * 2 / mol.sats) * s;
          const sx = mol.x + Math.cos(angle) * mol.satDist;
          const sy = mol.y + Math.sin(angle) * mol.satDist;
          ctx.beginPath();
          ctx.moveTo(mol.x, mol.y);
          ctx.lineTo(sx, sy);
          ctx.strokeStyle = `rgba(255,255,255,${mol.opacity * 0.3})`;
          ctx.lineWidth = 0.6;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(sx, sy, mol.r * 0.4, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${mol.opacity * 0.6})`;
          ctx.fill();
        }
      });

      // Connections
      for (let i = 0; i < molecules.length; i++) {
        for (let j = i + 1; j < molecules.length; j++) {
          const dx = molecules[i].x - molecules[j].x;
          const dy = molecules[i].y - molecules[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            ctx.beginPath();
            ctx.moveTo(molecules[i].x, molecules[i].y);
            ctx.lineTo(molecules[j].x, molecules[j].y);
            ctx.strokeStyle = `rgba(255,255,255,${(1 - dist / CONNECTION_DIST) * 0.04})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // Crosses
      crosses.forEach((c) => {
        c.y += c.vy;
        c.x += c.vx;
        c.rotation += c.rotSpeed;
        if (c.y < -20) { c.y = H + 20; c.x = Math.random() * W; }
        ctx.save();
        ctx.translate(c.x, c.y);
        ctx.rotate(c.rotation);
        ctx.strokeStyle = `rgba(255,255,255,${c.opacity})`;
        ctx.lineWidth = 1.2;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(0, -c.size);
        ctx.lineTo(0, c.size);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(-c.size, 0);
        ctx.lineTo(c.size, 0);
        ctx.stroke();
        ctx.restore();
      });

      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: -1,
      }}
    />
  );
};

export default HeroBioCanvas;
